import type { ReactNode } from 'react';
import Sidebar from '../Sidebar';
import { Download, ArrowLeft, Printer } from 'lucide-react';

interface ReportLayoutProps {
  title: string;
  description?: string;
  onBack?: () => void;
  backLabel?: string;
  filters?: ReactNode;
  onExport?: () => void;
  onPrint?: () => void;
  children: ReactNode;
}

export const ReportLayout = ({
  title,
  description,
  onBack,
  backLabel = 'Back',
  filters,
  onExport,
  onPrint,
  children
}: ReportLayoutProps) => {
  return (
    <div className="h-screen bg-gray-100 flex w-full overflow-hidden">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-y-auto min-w-0">
        <div className="w-full">
          <div className="mb-6">
            {onBack && (
              <button
                onClick={onBack}
                className="mb-4 inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {backLabel}
              </button>
            )}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                {description && (
                  <p className="mt-1 text-sm text-gray-500">{description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {onPrint && (
                  <button
                    onClick={onPrint}
                    className="inline-flex items-center justify-center p-2.5 text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg shadow-sm transition-colors"
                    title="Print"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                )}
                {onExport && (
                  <button
                    onClick={onExport}
                    className="inline-flex items-center justify-center p-2.5 text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg shadow-sm transition-colors"
                    title="Export"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {filters && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 md:p-5 mb-6">
              {filters}
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};
